import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { SignedIn, SignedOut, RedirectToSignIn } from '@clerk/clerk-react';
import { Toaster } from 'sonner';
import { useSettingsStore } from '@/stores/settingsStore';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { Spinner } from '@/components/ui/Spinner';

// Lazy-loaded pages (route-based code splitting)
const Home = lazy(() => import('@/pages/Home').then((m) => ({ default: m.Home })));
const Game = lazy(() => import('@/pages/Game').then((m) => ({ default: m.Game })));
const Leaderboard = lazy(() => import('@/pages/Leaderboard').then((m) => ({ default: m.Leaderboard })));
const Achievements = lazy(() => import('@/pages/Achievements').then((m) => ({ default: m.Achievements })));
const Profile = lazy(() => import('@/pages/Profile').then((m) => ({ default: m.Profile })));
const Settings = lazy(() => import('@/pages/Settings').then((m) => ({ default: m.Settings })));
const SignUp = lazy(() => import('@/pages/SignUp').then((m) => ({ default: m.SignUp })));
const HowToPlay = lazy(() => import('@/pages/HowToPlay').then((m) => ({ default: m.HowToPlay })));

// Layout
import { Layout } from '@/components/layout/Layout';
import { ProfileGuard } from '@/components/layout/ProfileGuard';

function PageFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <Spinner size="lg" />
    </div>
  );
}

export function App() {
  // Initialize settings (theme, audio) from persisted store
  // The settingsStore will handle initialization via onRehydrateStorage
  useSettingsStore();

  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <Toaster position="top-center" richColors closeButton />
      
      <ErrorBoundary>
      <Suspense fallback={<PageFallback />}>
      <Routes>
        {/* Public routes */}
        <Route path="/signup" element={<SignUp />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <>
              <SignedIn>
                <ProfileGuard>
                  <Layout>
                    <Home />
                  </Layout>
                </ProfileGuard>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </>
          }
        />

        <Route
          path="/game"
          element={
            <>
              <SignedIn>
                <ProfileGuard>
                  <Layout>
                    <Game />
                  </Layout>
                </ProfileGuard>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </>
          }
        />

        <Route
          path="/leaderboard"
          element={
            <>
              <SignedIn>
                <ProfileGuard>
                  <Layout>
                    <Leaderboard />
                  </Layout>
                </ProfileGuard>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </>
          }
        />

        <Route
          path="/achievements"
          element={
            <>
              <SignedIn>
                <ProfileGuard>
                  <Layout>
                    <Achievements />
                  </Layout>
                </ProfileGuard>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </>
          }
        />

        <Route
          path="/profile"
          element={
            <>
              <SignedIn>
                <ProfileGuard>
                  <Layout>
                    <Profile />
                  </Layout>
                </ProfileGuard>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </>
          }
        />

        <Route
          path="/settings"
          element={
            <>
              <SignedIn>
                <ProfileGuard>
                  <Layout>
                    <Settings />
                  </Layout>
                </ProfileGuard>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </>
          }
        />

        <Route
          path="/how-to-play"
          element={
            <>
              <SignedIn>
                <ProfileGuard>
                  <Layout>
                    <HowToPlay />
                  </Layout>
                </ProfileGuard>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </>
          }
        />
      </Routes>
      </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
